import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, Resolver, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { formatData } from "../../common";
import { genderListSelect, roleListSelect, STATUS } from "../../constant";
import { useGetAllBranch } from "../../hooks/query/useGetAllBranch";
import { useGetAllCourse } from "../../hooks/query/useGetAllCourse";
import { useGetAllSubject } from "../../hooks/query/useGetAllSubject";
import { AppDispatch, RootState } from "../../store";
import { createStaff } from "../../store/slices/authSlice";
import Button from "../Button";
import InputArea from "../InputArea";
import InputDate from "../InputDate";
import InputSelect from "../InputSelect";
import InputText from "../InputText";
import Loading from "../Loading";
import NotionPopup from "../NotionPopup";

const schema = yup.object({
    fullName: yup.string().required("Họ và tên  không được để trống"),
    birthDay: yup.string().required("Ngày sinh không được để trống"),
    email: yup.string().email("Email học sinh không hợp lệ").required("Email không được để trống"),
    password: yup
        .string()
        .matches(
            /^(?=.*[A-Za-z])(?=.*\d).{6,}$/,
            "Mật khẩu phải có ít nhất 6 ký tự, gồm chữ và số"
        )
        .required("Mật khẩu không được để trống"),
    phone: yup
        .string()
        .matches(/^(0|\+84)[0-9]{9,10}$/, "Số điện thoại không hợp lệ")
        .required("Số điện thoại là bắt buộc"),
    major: yup.string().required("Chuyên ngành không được để trống"),
    address: yup.string().required("Địa chỉ không được để trống"),
    course: yup.string().nullable().default(null),
    subject: yup.string().nullable().default(null),
    branch: yup.string().required("Cơ sở không được để trống"),
    startDate: yup.string().required("Ngày bắt đầu không được để trống"),
    gender: yup.string().required("Giới tính không được để trống"),
    role: yup.string().required("Chức vụ không được để trống"),
    description: yup.string().nullable().default(null),
});

type FormData = yup.InferType<typeof schema>;

function FormRegisterStaff() {
    const dispatch = useDispatch<AppDispatch>();
    // const { loading, error } = useSelector((state: RootState) => state.auth);
    const { loading } = useSelector((state: RootState) => state.auth);

    const [formData, setFormData] = useState<{
        email: string;
        password: string;
        fullName: string;
        gender: string;
        dateOfBirth: string;
        phoneNumber: string;
        address: string;
        avatarUrl: string;
        userType: string;
        major: string;
        academicTitle: string | null;
        staffType: string;
        schoolGrade: number;
        basicSalary: number;
        startDate: string;
        status: string;
        branchCode: string;
        courseCode?: string[] | string;
        subjectCode?: string[] | string;
        description: string;
    }>({
        email: "",
        password: "",
        fullName: "",
        gender: "other",
        dateOfBirth: "",
        phoneNumber: "",
        address: "",
        avatarUrl: "",
        userType: "staff",
        major: "",
        academicTitle: null,
        staffType: "teacher",
        schoolGrade: 0,
        basicSalary: 0,
        startDate: "",
        status: "pending",
        branchCode: "",
        courseCode: [], // 
        subjectCode: [], // 
        description: "",
    })
    const { control, handleSubmit, formState: { errors }, setFocus, setError, reset } = useForm<FormData>({
        resolver: yupResolver(schema) as Resolver<FormData>,
    });

    const onSubmit = async (data: FormData) => {
        let newData = {
            ...formData,
            email: data.email,
            password: data.password,
            fullName: data.fullName,
            gender: data.gender,
            dateOfBirth: data.birthDay,
            phoneNumber: data.phone,
            address: data.address,
            avatarUrl: "",
            userType: "staff",
            status: "pending",
            academicTitle: null,
            staffType: data.role,
            major: data.major,
            schoolGrade: 0,
            basicSalary: 0,
            startDate: data.startDate,
            branchCode: data.branch,
            courseCode: data.course ? [data.course] : [],
            subjectCode: data.subject ? [data.subject] : [],
            description: ""
        }
        if (data.role === "student_management") {
            newData.courseCode = [];
            newData.subjectCode = [];
        }
        setFormData(newData);
        const cleanedData = Object.fromEntries(
            Object.entries(newData).filter(([_, value]) => value !== "")
        );
        try {
            await dispatch(createStaff(cleanedData)).unwrap();
            // Nếu thành công, reset form và show popup (như bạn đang làm)
            setShowPopup(true);
            reset({
                fullName: "",
                birthDay: "",
                email: "",
                password: "",
                address: "",
                phone: "",
                major: "",
                course: "",
                subject: "",
                branch: "",
                startDate: "",
                gender: "other",
                role: "",
                description: "",
            });
            setTimeout(() => setShowPopup(false), 3000);
        } catch (err: any) {
            if (err?.status === STATUS.BAD_REQUEST && err?.data?.message.includes("already")) {
                setError("email", { type: "server", message: "Email này đã được sử dụng" });
                setFocus("email");
            } else {
                console.log("Lỗi server khác:", err?.data?.message || err);
            }
        }
    };

    const onError = (err: any) => {
        const firstErrorField = Object.keys(err)[0];
        if (firstErrorField) setFocus(firstErrorField as keyof FormData);
    };

    const [showPopup, setShowPopup] = useState(false);
    const handleClosePopup = () => {
        setShowPopup(false);
    };

    const selectedRole = useWatch({
        control,
        name: "role",
    });

    const { data: branchData } = useGetAllBranch(1, 10);
    const { data: courseData } = useGetAllCourse(1, 10);
    const { data: subjectData } = useGetAllSubject();

    const branchList = formatData(branchData?.data.items);
    const courseList = formatData(courseData?.data?.items);
    const subjectList = formatData(subjectData || []);
    return (
        <>{loading && (<Loading />)}
            {showPopup && <NotionPopup onClose={handleClosePopup} />} {/* Hiển thị popup khi success */}
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="flex flex-col mx-auto"
            >
                <div className="flex flex-col lg:flex-row gap-x-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)]">
                        <Controller
                            name="fullName"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Họ và tên *"
                                    error={errors.fullName?.message}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="birthDay"
                            control={control}
                            render={({ field }) => (
                                <InputDate
                                    {...field}
                                    label="Ngày sinh *"
                                    error={errors.birthDay?.message}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)]">
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Email *"
                                    error={errors.email?.message}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Mật khẩu"
                                    error={errors.password?.message}
                                    type="password"
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)]">
                        <Controller
                            name="address"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Địa chỉ *"
                                    error={errors.address?.message}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Số điện thoại *"
                                    error={errors.phone?.message}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)]">
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    label="Chức vụ *"
                                    error={errors.role?.message}
                                    data={roleListSelect}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="branch"
                            control={control}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    label="Chọn cơ sở làm việc *"
                                    error={errors.branch?.message}
                                    data={branchList}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    label="Giới tính *"
                                    error={errors.gender?.message}
                                    data={genderListSelect}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="startDate"
                            control={control}
                            render={({ field }) => (
                                <InputDate
                                    {...field}
                                    label="Ngày bắt đầu dự kiến *"
                                    error={errors.startDate?.message}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)]">

                        <Controller
                            name="major"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    label="Chuyên ngành *"
                                    error={errors.major?.message}
                                />
                            )}
                        />
                    </div>
                    {selectedRole === "teacher" && <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="course"
                            control={control}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    label="Chọn khóa học dạy"
                                    error={errors.course?.message}
                                    data={courseList}
                                />
                            )}
                        />
                    </div>}
                </div>
                {selectedRole === "teacher" && <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        <Controller
                            name="subject"
                            control={control}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    label="Môn dạy"
                                    error={errors.subject?.message}
                                    data={subjectList}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-[calc(50%-20px)] mt-[40px] lg:mt-0">
                        {/* <Controller
                            name="startDate"
                            control={control}
                            render={({ field }) => (
                                <InputDate
                                    {...field}
                                    label="Ngày bắt đầu dự kiến *"
                                    error={errors.startDate?.message}
                                />
                            )}
                        /> */}
                    </div>
                </div>}
                <div className="flex flex-col lg:flex-row gap-x-[40px] mt-[40px]">
                    <div className="w-full">
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <InputArea
                                    {...field}
                                    label="Ghi chú thêm"
                                    error={errors.description?.message}
                                    placeholder="Thông tin bổ sung ..."
                                />
                            )}
                        />
                    </div>
                </div>
                <Button type="submit" label="Đăng ký" styleButton="w-[140px] h-[45px] mx-auto mt-[40px]" />
            </form>
        </>
    );
}

export default FormRegisterStaff;
